import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  testChannel,
  fetchSubscriptions,
  saveSubscription,
  deleteSubscription,
  fetchNotificationLog,
  fetchEventTypes,
} from '../../lib/api';
import type {
  NotificationChannel,
  NotificationSubscription,
  NotificationLogEntry,
  EventType,
} from '../../types';
import { safeArray } from '../../lib/safe';

export const Route = createFileRoute('/settings/notifications')({
  component: NotificationsPage,
});

// ── Tab type ──────────────────────────────────────────
type Tab = 'channels' | 'subscriptions' | 'log';

const TABS: { key: Tab; label: string }[] = [
  { key: 'channels', label: 'Channels' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'log', label: 'Log' },
];

// ── Channel type icons ────────────────────────────────
function ChannelTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'telegram':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.198 2.433a2.242 2.242 0 0 0-1.022.215l-8.609 3.33c-2.068.8-4.133 1.598-5.724 2.21a405.15 405.15 0 0 1-2.849 1.09c-.42.147-.99.332-1.473.901-.728.855.14 1.678.399 1.904l3.75 3.03 1.59 4.7c.216.638.545 1.08 1.04 1.18.496.1.898-.15 1.18-.37l2.25-1.72 3.63 2.75c.6.45 1.29.48 1.77.18.48-.3.72-.9.84-1.44l2.99-15.48c.1-.56.09-1.2-.42-1.69-.27-.26-.67-.4-1.05-.43a2.1 2.1 0 0 0-.29-.01z" />
        </svg>
      );
    case 'slack':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="13" y="2" width="3" height="8" rx="1.5" /><path d="M19 8.5V10h1.5A1.5 1.5 0 1019 8.5" />
          <rect x="8" y="14" width="3" height="8" rx="1.5" /><path d="M5 15.5V14H3.5A1.5 1.5 0 105 15.5" />
          <rect x="14" y="13" width="8" height="3" rx="1.5" /><path d="M15.5 19H14v1.5a1.5 1.5 0 101.5-1.5" />
          <rect x="2" y="8" width="8" height="3" rx="1.5" /><path d="M8.5 5H10V3.5A1.5 1.5 0 108.5 5" />
        </svg>
      );
    case 'email':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" />
        </svg>
      );
    case 'webhook':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 012 17c.01-.7.2-1.4.57-2" />
          <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 116.89-4.06" />
          <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 010 8H12" />
        </svg>
      );
    default:
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
  }
}

// ── Main page ─────────────────────────────────────────
function NotificationsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('channels');

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[var(--ccb-text)]">Notification Settings</h2>

      {/* Tab bar */}
      <div className="flex gap-1 bg-white rounded-xl border border-[var(--ccb-border)] p-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-[var(--ccb-primary)] text-white'
                : 'text-[var(--ccb-text-secondary)] hover:bg-slate-50 hover:text-[var(--ccb-text)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'channels' && <ChannelsTab />}
      {activeTab === 'subscriptions' && <SubscriptionsTab />}
      {activeTab === 'log' && <LogTab />}
    </div>
  );
}

// ── Channels Tab ──────────────────────────────────────
const CHANNEL_TYPES = ['telegram', 'slack', 'email', 'webhook'] as const;

interface ChannelFormData {
  channel_type: typeof CHANNEL_TYPES[number];
  name: string;
  config: Record<string, any>;
}

function emptyForm(): ChannelFormData {
  return { channel_type: 'telegram', name: '', config: {} };
}

function ChannelsTab() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ChannelFormData>(emptyForm());
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; msg?: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () => fetchChannels(),
  });

  const channels = safeArray(data?.channels);

  const createMutation = useMutation({
    mutationFn: (body: Partial<NotificationChannel>) => createChannel(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      closeForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<NotificationChannel> }) => updateChannel(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteChannel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-channels'] });
      queryClient.invalidateQueries({ queryKey: ['notification-subscriptions'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => testChannel(id),
    onSuccess: (res, id) => setTestResult({ id, ok: res.success, msg: res.error }),
    onError: (err, id) => setTestResult({ id, ok: false, msg: (err as Error).message }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: number }) => updateChannel(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-channels'] }),
  });

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  }

  function openEdit(ch: NotificationChannel) {
    setForm({ channel_type: ch.channel_type, name: ch.name, config: ch.config ?? {} });
    setEditingId(ch.id);
    setShowForm(true);
  }

  function handleSave() {
    const body: Partial<NotificationChannel> = {
      channel_type: form.channel_type,
      name: form.name,
      config: form.config,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, body });
    } else {
      createMutation.mutate(body);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--ccb-text-secondary)]">
          Manage notification delivery channels
        </p>
        {!showForm && (
          <button
            onClick={() => { setForm(emptyForm()); setEditingId(null); setShowForm(true); }}
            className="text-sm font-medium px-4 py-2 rounded-lg bg-[var(--ccb-primary)] text-white hover:opacity-90 transition-opacity"
          >
            Add Channel
          </button>
        )}
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-[var(--ccb-border)] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[var(--ccb-text)]">
            {editingId ? 'Edit Channel' : 'New Channel'}
          </h3>

          {/* Channel type selector */}
          <div>
            <label className="block text-xs font-medium text-[var(--ccb-text-secondary)] mb-1">Channel Type</label>
            <div className="flex gap-2">
              {CHANNEL_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setForm((f) => ({ ...f, channel_type: t, config: {} }))}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm capitalize transition-colors ${
                    form.channel_type === t
                      ? 'border-[var(--ccb-primary)] bg-blue-50 text-[var(--ccb-primary)] font-medium'
                      : 'border-[var(--ccb-border)] text-[var(--ccb-text-secondary)] hover:bg-slate-50'
                  }`}
                >
                  <ChannelTypeIcon type={t} />
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-[var(--ccb-text-secondary)] mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Production Alerts"
              className="w-full px-3 py-2 rounded-lg border border-[var(--ccb-border)] text-sm text-[var(--ccb-text)] placeholder:text-[var(--ccb-text-secondary)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30 focus:border-[var(--ccb-primary)]"
            />
          </div>

          {/* Type-specific config fields */}
          <ChannelConfigFields
            type={form.channel_type}
            config={form.config}
            onChange={(config) => setForm((f) => ({ ...f, config }))}
          />

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={isSaving || !form.name.trim()}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-[var(--ccb-primary)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            {editingId && (
              <button
                onClick={() => { testMutation.mutate(editingId); }}
                disabled={testMutation.isPending}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-[var(--ccb-border)] text-[var(--ccb-text)] hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                {testMutation.isPending ? 'Testing...' : 'Test'}
              </button>
            )}
            <button
              onClick={closeForm}
              className="text-sm text-[var(--ccb-text-secondary)] hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Channel list */}
      <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">Loading channels...</div>
        ) : channels.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">
            No channels configured. Add one to start receiving notifications.
          </div>
        ) : (
          <div className="divide-y divide-[var(--ccb-border)]">
            {channels.map((ch: NotificationChannel) => (
              <div key={ch.id} className="px-4 py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-[var(--ccb-text-secondary)] shrink-0">
                    <ChannelTypeIcon type={ch.channel_type} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--ccb-text)] truncate">{ch.name}</p>
                    <p className="text-xs text-[var(--ccb-text-secondary)] capitalize">{ch.channel_type}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  {/* Test result badge */}
                  {testResult && testResult.id === ch.id && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {testResult.ok ? 'Sent' : testResult.msg ?? 'Failed'}
                    </span>
                  )}

                  {/* Enabled toggle */}
                  <button
                    onClick={() => toggleMutation.mutate({ id: ch.id, enabled: ch.enabled ? 0 : 1 })}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      ch.enabled ? 'bg-[var(--ccb-primary)]' : 'bg-slate-300'
                    }`}
                    title={ch.enabled ? 'Enabled' : 'Disabled'}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      ch.enabled ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>

                  {/* Test button */}
                  <button
                    onClick={() => testMutation.mutate(ch.id)}
                    disabled={testMutation.isPending}
                    className="text-xs font-medium text-[var(--ccb-text-secondary)] hover:text-[var(--ccb-text)] hover:underline disabled:opacity-50"
                  >
                    Test
                  </button>

                  {/* Edit button */}
                  <button
                    onClick={() => openEdit(ch)}
                    className="text-xs font-medium text-[var(--ccb-primary)] hover:underline"
                  >
                    Edit
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete channel "${ch.name}"?`)) {
                        deleteMutation.mutate(ch.id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Channel config fields per type ────────────────────
function ChannelConfigFields({
  type,
  config,
  onChange,
}: {
  type: string;
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}) {
  function set(key: string, value: string) {
    onChange({ ...config, [key]: value });
  }

  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-[var(--ccb-border)] text-sm text-[var(--ccb-text)] placeholder:text-[var(--ccb-text-secondary)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--ccb-primary)]/30 focus:border-[var(--ccb-primary)]';
  const labelClass = 'block text-xs font-medium text-[var(--ccb-text-secondary)] mb-1';

  switch (type) {
    case 'telegram':
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Bot Token</label>
            <input type="text" value={config.bot_token ?? ''} onChange={(e) => set('bot_token', e.target.value)} placeholder="123456:ABC-DEF..." className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Chat ID</label>
            <input type="text" value={config.chat_id ?? ''} onChange={(e) => set('chat_id', e.target.value)} placeholder="-100123456789" className={inputClass} />
          </div>
        </div>
      );
    case 'slack':
      return (
        <div>
          <label className={labelClass}>Webhook URL</label>
          <input type="url" value={config.webhook_url ?? ''} onChange={(e) => set('webhook_url', e.target.value)} placeholder="https://hooks.slack.com/services/..." className={inputClass} />
        </div>
      );
    case 'email':
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>From Address</label>
            <input type="email" value={config.from ?? ''} onChange={(e) => set('from', e.target.value)} placeholder="alerts@example.com" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>To Address(es)</label>
            <input type="text" value={config.to ?? ''} onChange={(e) => set('to', e.target.value)} placeholder="team@example.com, ops@example.com" className={inputClass} />
          </div>
        </div>
      );
    case 'webhook':
      return (
        <div className="space-y-4">
          <div>
            <label className={labelClass}>URL</label>
            <input type="url" value={config.url ?? ''} onChange={(e) => set('url', e.target.value)} placeholder="https://api.example.com/webhook" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Headers (JSON, optional)</label>
            <textarea
              value={config.headers ?? ''}
              onChange={(e) => set('headers', e.target.value)}
              placeholder='{"Authorization": "Bearer ..."}'
              rows={3}
              className={`${inputClass} resize-none font-mono text-xs`}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
}

// ── Subscriptions Tab ─────────────────────────────────
function SubscriptionsTab() {
  const queryClient = useQueryClient();

  const { data: channelsData, isLoading: channelsLoading } = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () => fetchChannels(),
  });

  const { data: subsData, isLoading: subsLoading } = useQuery({
    queryKey: ['notification-subscriptions'],
    queryFn: () => fetchSubscriptions(),
  });

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['notification-event-types'],
    queryFn: () => fetchEventTypes(),
  });

  const channels = safeArray(channelsData?.channels);
  const subscriptions = safeArray(subsData?.subscriptions);
  const eventTypes = safeArray(eventsData?.event_types);

  const saveMutation = useMutation({
    mutationFn: (body: Partial<NotificationSubscription>) => saveSubscription(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-subscriptions'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSubscription(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-subscriptions'] }),
  });

  function findSub(eventType: string, channelId: string): NotificationSubscription | undefined {
    return subscriptions.find(
      (s: NotificationSubscription) => s.event_type === eventType && s.channel_id === channelId,
    );
  }

  function handleToggle(eventType: string, channelId: string) {
    const existing = findSub(eventType, channelId);
    if (existing) {
      deleteMutation.mutate(existing.id);
    } else {
      saveMutation.mutate({ channel_id: channelId, event_type: eventType, enabled: 1 });
    }
  }

  const isLoading = channelsLoading || subsLoading || eventsLoading;
  const isMutating = saveMutation.isPending || deleteMutation.isPending;

  // Group event types by category
  const categories = eventTypes.reduce<Record<string, EventType[]>>((acc, et: EventType) => {
    const cat = et.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(et);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--ccb-text-secondary)]">
        Choose which events are delivered to each channel
      </p>

      <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">Loading subscriptions...</div>
        ) : channels.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">
            No channels configured. Add a channel first.
          </div>
        ) : eventTypes.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">
            No event types available.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--ccb-border)]">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide sticky left-0 bg-white z-10">
                    Event Type
                  </th>
                  {channels.map((ch: NotificationChannel) => (
                    <th
                      key={ch.id}
                      className="text-center px-3 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide whitespace-nowrap"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <ChannelTypeIcon type={ch.channel_type} />
                        <span>{ch.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(categories).map(([category, events]) => (
                  <CategoryGroup
                    key={category}
                    category={category}
                    events={events}
                    channels={channels}
                    findSub={findSub}
                    onToggle={handleToggle}
                    disabled={isMutating}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryGroup({
  category,
  events,
  channels,
  findSub,
  onToggle,
  disabled,
}: {
  category: string;
  events: EventType[];
  channels: NotificationChannel[];
  findSub: (eventType: string, channelId: string) => NotificationSubscription | undefined;
  onToggle: (eventType: string, channelId: string) => void;
  disabled: boolean;
}) {
  return (
    <>
      {/* Category header row */}
      <tr className="bg-slate-50/70">
        <td
          colSpan={channels.length + 1}
          className="px-4 py-2 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide"
        >
          {category}
        </td>
      </tr>
      {events.map((et: EventType) => (
        <tr key={et.key} className="border-b border-[var(--ccb-border)] last:border-0 hover:bg-slate-50/50">
          <td className="px-4 py-2.5 text-sm text-[var(--ccb-text)] font-medium sticky left-0 bg-white">
            {et.event_type}
          </td>
          {channels.map((ch: NotificationChannel) => {
            const sub = findSub(et.event_type, ch.id);
            return (
              <td key={ch.id} className="text-center px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={!!sub}
                  onChange={() => onToggle(et.event_type, ch.id)}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-[var(--ccb-border)] text-[var(--ccb-primary)] focus:ring-[var(--ccb-primary)]/30 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// ── Log Tab ───────────────────────────────────────────
function LogTab() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: logData, isLoading: logLoading } = useQuery({
    queryKey: ['notification-log'],
    queryFn: () => fetchNotificationLog(100),
    refetchInterval: 10_000,
  });

  const { data: channelsData } = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () => fetchChannels(),
  });

  const log = safeArray(logData?.log);
  const channels = safeArray(channelsData?.channels);

  function channelName(channelId: string): string {
    const ch = channels.find((c: NotificationChannel) => c.id === channelId);
    return ch ? ch.name : channelId;
  }

  function statusBadge(status: string) {
    switch (status) {
      case 'sent':
        return 'bg-emerald-50 text-emerald-700';
      case 'failed':
        return 'bg-red-50 text-red-700';
      case 'queued':
      default:
        return 'bg-slate-100 text-slate-600';
    }
  }

  function tryFormatPayload(raw: string): string {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--ccb-text-secondary)]">
        Last 100 notification deliveries
      </p>

      <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
        {logLoading ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">Loading log...</div>
        ) : log.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">
            No notification log entries yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--ccb-border)]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Event Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Channel</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Error</th>
              </tr>
            </thead>
            <tbody>
              {log.map((entry: NotificationLogEntry) => (
                <LogRow
                  key={entry.id}
                  entry={entry}
                  channelName={channelName(entry.channel_id)}
                  statusBadge={statusBadge(entry.status)}
                  expanded={expandedId === entry.id}
                  onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  formatPayload={tryFormatPayload}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LogRow({
  entry,
  channelName,
  statusBadge,
  expanded,
  onToggle,
  formatPayload,
}: {
  entry: NotificationLogEntry;
  channelName: string;
  statusBadge: string;
  expanded: boolean;
  onToggle: () => void;
  formatPayload: (raw: string) => string;
}) {
  return (
    <>
      <tr
        className="border-b border-[var(--ccb-border)] last:border-0 hover:bg-slate-50/50 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-3 text-xs text-[var(--ccb-text-secondary)] whitespace-nowrap">
          {new Date(entry.created_at).toLocaleString()}
        </td>
        <td className="px-4 py-3 text-sm text-[var(--ccb-text)] font-medium">
          {entry.event_type}
        </td>
        <td className="px-4 py-3 text-sm text-[var(--ccb-text-secondary)]">
          {channelName}
        </td>
        <td className="px-4 py-3">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadge}`}>
            {entry.status}
          </span>
        </td>
        <td className="px-4 py-3 text-xs text-red-600 truncate max-w-[250px]">
          {entry.error || '—'}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50">
          <td colSpan={5} className="px-4 py-3">
            <div className="text-xs font-medium text-[var(--ccb-text-secondary)] mb-1">Payload</div>
            <pre className="text-xs bg-slate-100 rounded-lg p-3 overflow-x-auto max-h-64 text-[var(--ccb-text)] font-mono whitespace-pre-wrap">
              {entry.payload ? formatPayload(entry.payload) : '(empty)'}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
